import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { products } from '@/mocks/products';

interface SearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  placeholder?: string;
}

interface SearchResult {
  id: string;
  name: string;
  slug: string;
  brand: string;
  price: number;
  salePrice: number | null;
  image: string;
  categoryName: string;
}

export default function SearchBar({
  searchQuery,
  onSearchChange,
  placeholder = 'Search 119 products...',
}: SearchBarProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo<SearchResult[]>(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const q = searchQuery.toLowerCase().trim();
    return products
      .filter((p) => {
        return (
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)) ||
          p.categoryName.toLowerCase().includes(q)
        );
      })
      .slice(0, 8)
      .map((p) => ({
        id: p.id,
        name: p.name,
        slug: p.slug,
        brand: p.brand,
        price: p.price,
        salePrice: p.salePrice,
        image: p.images[0],
        categoryName: p.categoryName,
      }));
  }, [searchQuery]);

  const selectResult = useCallback(
    (result: SearchResult) => {
      onSearchChange('');
      setIsOpen(false);
      setHighlightIndex(-1);
      navigate(`/product/${result.slug}`);
    },
    [navigate, onSearchChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < results.length) {
        selectResult(results[highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  };

  useEffect(() => {
    if (searchQuery.length >= 2) {
      setIsOpen(true);
      setHighlightIndex(-1);
    } else {
      setIsOpen(false);
      setHighlightIndex(-1);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightIndex]) {
        (items[highlightIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightIndex]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  const handleClear = () => {
    onSearchChange('');
    setIsOpen(false);
    setHighlightIndex(-1);
    inputRef.current?.focus();
  };

  const displayPrice = (item: SearchResult) => {
    if (item.salePrice) {
      return (
        <span className="flex items-center gap-1.5">
          <span className="font-semibold text-foreground-950">KSh {item.salePrice.toLocaleString()}</span>
          <span className="text-xs text-foreground-400 line-through">KSh {item.price.toLocaleString()}</span>
        </span>
      );
    }
    return <span className="font-semibold text-foreground-950">KSh {item.price.toLocaleString()}</span>;
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <i className="ri-search-line text-foreground-400 text-lg" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchQuery.length >= 2) setIsOpen(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full h-11 pl-10 pr-10 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-400 hover:text-foreground-600 transition-colors"
          >
            <i className="ri-close-circle-fill text-lg" />
          </button>
        )}
      </div>

      {/* Autocomplete dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-background-200/60">
            <span className="text-xs font-medium text-foreground-500">
              {results.length} result{results.length !== 1 ? 's' : ''} for &quot;{searchQuery}&quot;
            </span>
            <span className="text-xs text-foreground-400">
              <kbd className="px-1.5 py-0.5 rounded bg-background-200 text-foreground-500 text-[10px]">ESC</kbd> to close
            </span>
          </div>
          <ul ref={listRef} className="max-h-80 overflow-y-auto py-1">
            {results.map((item, index) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => selectResult(item)}
                  onMouseEnter={() => setHighlightIndex(index)}
                  className={`flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors ${
                    index === highlightIndex ? 'bg-primary-50' : 'hover:bg-background-100'
                  }`}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-background-200/50"
                    loading="lazy"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{item.name}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {item.brand} &middot; {item.categoryName}
                    </p>
                  </div>
                  <div className="text-sm flex-shrink-0">{displayPrice(item)}</div>
                </button>
              </li>
            ))}
          </ul>
          <div className="px-4 py-2 border-t border-background-200/60">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                setHighlightIndex(-1);
                inputRef.current?.blur();
              }}
              className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <i className="ri-arrow-down-line" />
              Press Enter to browse all &quot;{searchQuery}&quot; results below
            </button>
          </div>
        </div>
      )}

      {isOpen && searchQuery.length >= 2 && results.length === 0 && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/70 shadow-lg p-6 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-search-line text-xl text-foreground-400" />
          </div>
          <p className="text-sm font-medium text-foreground-700">No results found</p>
          <p className="text-xs text-foreground-500 mt-1">
            No products match &quot;{searchQuery}&quot;. Try a different keyword.
          </p>
        </div>
      )}
    </div>
  );
}